import React, { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

// UIKit Components
import { FlexBox } from '@spazious/react-components';

import {
  DurationSelectorUnitField,
  DurationSelectorUnitFieldChangeEvent,
  DurationSelectorUnitFieldProps,
} from './DurationSelectorUnitField.js';

dayjs.extend(duration);

export type DurationSelectorUnitsProps = Partial<
  Record<duration.DurationUnitType, Partial<DurationSelectorUnitFieldProps>>
>;

export type DurationSelectorProps = {
  /**
   * The value of the duration in ISO 8601 Durations format.
   */
  value: string;
  onChange?: (evt: DurationSelectorUnitFieldChangeEvent, value: string) => void;
  /**
   * User can hold the button to increase/decrease the value
   */
  hold?: boolean;
  /**
   * The units to display in the duration selector (e.g. 'yMdhms')
   */
  units?: string;
  /**
   * The props for each unit field
   */
  unitsProps?: DurationSelectorUnitsProps;
  /**
   * When true, the component will be disabled
   */
  disabled?: boolean;
};

export const defaultUnitsProps: DurationSelectorUnitsProps = {
  y: {
    label: 'years',
    minValue: 0,
    maxValue: Infinity,
  },
  M: {
    label: 'months',
    minValue: 0,
    maxValue: Infinity,
  },
  d: {
    label: 'days',
    minValue: 0,
    maxValue: Infinity,
  },
  h: {
    label: 'hours',
    minValue: 0,
    maxValue: 23,
  },
  m: {
    label: 'minutes',
    minValue: 0,
    maxValue: 59,
  },
  s: {
    label: 'seconds',
    minValue: 0,
    maxValue: 59,
  },
};

/**
 * Duration selector is a component that allows you to select a duration in hours, minutes and seconds.
 * It is a controlled component, so you need to pass the value and the onChange handler.
 *
 * ISO 8601 Durations
 * @example
 * PT12H30M5S
 */
export const DurationSelector: React.FC<DurationSelectorProps> = ({
  value,
  onChange,
  units = 'yMdhms',
  unitsProps = {},
  hold,
  disabled,
  ...restProps
}) => {
  const parsedDuration = dayjs.duration(value);
  const unitsArr = useMemo(() => Array.from(units) as duration.DurationUnitType[], [units]);

  const handleChange = useCallback<PickProp<DurationSelectorUnitFieldProps, 'onChange'>>(
    (evt, value) => {
      const durationObj = unitsArr.reduce(
        (acc, unitKey) => ({
          ...acc,
          [unitKey]: parsedDuration.get(unitKey),
        }),
        {} as Partial<Record<duration.DurationUnitType, number>>
      );

      const newDuration = dayjs.duration({
        ...durationObj,
        [evt.unit]: value,
      });

      onChange?.(evt, newDuration.toJSON());
    },
    [onChange, parsedDuration, unitsArr]
  );

  const unitNodes = useMemo(
    () =>
      unitsArr.map((unitKey) => (
        <DurationSelectorUnitField
          key={unitKey}
          disabled={disabled}
          hold={hold}
          label={unitKey}
          unit={unitKey}
          value={`${parsedDuration.get(unitKey) ?? 0}`}
          onChange={handleChange}
          {...defaultUnitsProps[unitKey]}
          {...unitsProps[unitKey]}
        />
      )),
    [disabled, handleChange, hold, parsedDuration, unitsArr, unitsProps]
  );

  return (
    <FlexBox alignItems="center" direction="row" gap={1} {...restProps}>
      {unitNodes}
    </FlexBox>
  );
};

export default DurationSelector;
